import * as THREE from 'three/webgpu';

/**
 * 包围盒相机聚焦求解请求。
 */
export interface BoundingBoxCameraFocusRequest {
    /** 目标包围盒。 */
    box: THREE.Box3;
    /** 当前相机。 */
    camera: THREE.Camera;
    /** 可选视线方向；未传时优先沿用当前相机视线。 */
    direction?: THREE.Vector3;
    /** 可选上方向；未传时沿用当前相机 up。 */
    up?: THREE.Vector3;
    /** 额外留白系数。 */
    padding?: number;
}

/**
 * 包围盒相机聚焦结果。
 */
export interface BoundingBoxCameraFocusResult {
    /** 新相机位置。 */
    position: THREE.Vector3;
    /** 观察目标。 */
    target: THREE.Vector3;
    /** 新 up 方向。 */
    up: THREE.Vector3;
    /** 正交相机缩放值；透视相机下为空。 */
    zoom?: number;
}

/**
 * 包围盒相机聚焦求解器。
 *
 * 设计说明：
 * 1. 该类只负责“包围盒 -> 相机帧”的数学求解，不直接修改 Viewer 或 CameraManager 状态；
 * 2. CameraManager 负责选择对象、生成包围盒，并把求解结果应用到当前相机；
 * 3. 这样整屏显示全图、对象聚焦、后续局部缩放都能复用同一套相机能力。
 */
export class BoundingBoxCameraFocusSolver {
    private static readonly DEFAULT_PADDING = 1.15;
    private static readonly DEFAULT_DIRECTION = new THREE.Vector3(1, 1, 1).normalize();
    private static readonly DEFAULT_UP = new THREE.Vector3(0, 1, 0);

    /**
     * 根据包围盒求解相机聚焦结果。
     *
     * @param request 求解输入。
     * @returns 聚焦结果；包围盒为空时返回 null。
     */
    public resolve(request: BoundingBoxCameraFocusRequest): BoundingBoxCameraFocusResult | null {
        if (request.box.isEmpty()) {
            return null;
        }

        const target = request.box.getCenter(new THREE.Vector3());
        const direction = this.resolveDirection(request.camera, target, request.direction);
        const up = this.resolveUp(direction, request.up ?? request.camera.up ?? BoundingBoxCameraFocusSolver.DEFAULT_UP);
        const padding = request.padding ?? BoundingBoxCameraFocusSolver.DEFAULT_PADDING;

        if (request.camera instanceof THREE.PerspectiveCamera) {
            return this.resolvePerspectiveResult(request.box, request.camera, target, direction, up, padding);
        }

        if (request.camera instanceof THREE.OrthographicCamera) {
            return this.resolveOrthographicResult(request.box, request.camera, target, direction, up, padding);
        }

        return null;
    }

    /**
     * 解析透视相机的聚焦结果。
     *
     * @param box 包围盒。
     * @param camera 透视相机。
     * @param target 观察目标。
     * @param direction 视线方向。
     * @param up 上方向。
     * @param padding 留白系数。
     * @returns 聚焦结果。
     */
    private resolvePerspectiveResult(
        box: THREE.Box3,
        camera: THREE.PerspectiveCamera,
        target: THREE.Vector3,
        direction: THREE.Vector3,
        up: THREE.Vector3,
        padding: number,
    ): BoundingBoxCameraFocusResult {
        const right = direction.clone().cross(up).normalize();
        const actualUp = right.clone().cross(direction).normalize();
        const halfTanVertical = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
        const halfTanHorizontal = halfTanVertical * camera.aspect;
        const corners = this.collectCorners(box);

        let requiredDistance = 0;
        corners.forEach((corner) => {
            // 角点投影到相机局部坐标，用于同时满足横向、纵向和深度覆盖。
            const relative = corner.clone().sub(target);
            const horizontal = Math.abs(relative.dot(right)) * padding;
            const vertical = Math.abs(relative.dot(actualUp)) * padding;
            const depth = relative.dot(direction);
            requiredDistance = Math.max(
                requiredDistance,
                depth + horizontal / halfTanHorizontal,
                depth + vertical / halfTanVertical,
            );
        });

        const position = target.clone().add(direction.clone().multiplyScalar(requiredDistance));
        return {
            position,
            target,
            up: actualUp,
        };
    }

    /**
     * 解析正交相机的聚焦结果。
     *
     * @param box 包围盒。
     * @param camera 正交相机。
     * @param target 观察目标。
     * @param direction 视线方向。
     * @param up 上方向。
     * @param padding 留白系数。
     * @returns 聚焦结果。
     */
    private resolveOrthographicResult(
        box: THREE.Box3,
        camera: THREE.OrthographicCamera,
        target: THREE.Vector3,
        direction: THREE.Vector3,
        up: THREE.Vector3,
        padding: number,
    ): BoundingBoxCameraFocusResult {
        const right = direction.clone().cross(up).normalize();
        const actualUp = right.clone().cross(direction).normalize();
        const corners = this.collectCorners(box);
        let halfWidth = 0;
        let halfHeight = 0;
        corners.forEach((corner) => {
            // 正交投影只需要覆盖相机局部平面宽高，深度不影响屏幕尺寸。
            const relative = corner.clone().sub(target);
            halfWidth = Math.max(halfWidth, Math.abs(relative.dot(right)));
            halfHeight = Math.max(halfHeight, Math.abs(relative.dot(actualUp)));
        });

        const frustumWidth = Math.abs(camera.right - camera.left);
        const frustumHeight = Math.abs(camera.top - camera.bottom);
        const zoom = Math.min(
            frustumWidth / (halfWidth * 2 * padding || 1),
            frustumHeight / (halfHeight * 2 * padding || 1),
        );
        const distance = camera.position.distanceTo(target);
        const position = target.clone().add(direction.clone().multiplyScalar(distance));
        return {
            position,
            target,
            up: actualUp,
            zoom,
        };
    }

    /**
     * 解析视线方向。
     *
     * @param camera 当前相机。
     * @param target 观察目标。
     * @param direction 外部指定方向。
     * @returns 单位化后的视线方向。
     */
    private resolveDirection(
        camera: THREE.Camera,
        target: THREE.Vector3,
        direction?: THREE.Vector3,
    ): THREE.Vector3 {
        if (direction && direction.lengthSq() > 0) {
            return direction.clone().normalize();
        }

        const derived = camera.position.clone().sub(target);
        if (derived.lengthSq() > 0) {
            return derived.normalize();
        }

        return BoundingBoxCameraFocusSolver.DEFAULT_DIRECTION.clone();
    }

    /**
     * 解析并修正 up 向量，避免与视线方向平行。
     *
     * @param direction 视线方向。
     * @param up 候选上方向。
     * @returns 修正后的单位化 up 向量。
     */
    private resolveUp(direction: THREE.Vector3, up: THREE.Vector3): THREE.Vector3 {
        const normalizedUp = up.clone().normalize();
        if (Math.abs(direction.dot(normalizedUp)) < 0.999) {
            return normalizedUp;
        }
        return new THREE.Vector3(0, 0, 1);
    }

    /**
     * 收集包围盒八个角点。
     *
     * @param box 包围盒。
     * @returns 角点列表。
     */
    private collectCorners(box: THREE.Box3): THREE.Vector3[] {
        return [
            new THREE.Vector3(box.min.x, box.min.y, box.min.z),
            new THREE.Vector3(box.min.x, box.min.y, box.max.z),
            new THREE.Vector3(box.min.x, box.max.y, box.min.z),
            new THREE.Vector3(box.min.x, box.max.y, box.max.z),
            new THREE.Vector3(box.max.x, box.min.y, box.min.z),
            new THREE.Vector3(box.max.x, box.min.y, box.max.z),
            new THREE.Vector3(box.max.x, box.max.y, box.min.z),
            new THREE.Vector3(box.max.x, box.max.y, box.max.z),
        ];
    }
}
